import { DateTimeEditor, DropdownEditor, NumberEditor } from '@approvalmax/ui';
import { Box, ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { PlainDataProvider } from 'modules/data-providers';
import { useSelector } from 'modules/react-redux';
import moment from 'moment';
import { FC, memo, useCallback } from 'react';
import { useLifecycles } from 'react-use';
import { useUserBetaFeatures } from 'shared/hooks';
import { netSuiteExpenseLinesState } from 'shared/states';

import { AttachmentsSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { SectionContainer } from '../../../components/SectionContainer';
import { getNetSuiteContext } from '../../../selectors/netsuite/netSuiteSelectors';
import { getContext } from '../../../selectors/pageSelectors';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import {
    AmountInfoExpenseReport,
    NetSuiteAccountsCorporateCardDataProvider,
    NetSuiteCurrencySection,
    NetSuiteCustomFields,
} from '../components';
import { convertCustomFieldsToState } from '../helpers';
import { useCurrencyPrecision, useResetExpenseReportFormState } from '../hooks';
import { isFieldMandatory, isFieldVisible } from '../NetSuiteRequestForm/NetSuiteRequestForm.helpers';
import { companyIdState, netSuiteCustomFieldsState } from '../resources/states';
import { ExpenseLines, ExpenseSection } from './components';
import { checkAccountForCorporateCardIsInvalid } from './helpers/checkAccountForCorporateCardIsInvalid/checkAccountForCorporateCardIsInvalid';
import { useExpenseReportFilesUploadAttachments } from './hooks';
import { useExpenseLinesUpdate, useExpenseReportAmount, useExpenseReportExpenseLines } from './hooks';
import { useExpenseReportForm, useExpenseReportFormSubmit } from './NetSuiteExpenseReportRequestForm.hooks';
import { messages } from './NetSuiteExpenseReportRequestForm.messages';
import { netSuiteExpenseReportFieldValuesState } from './NetSuiteExpenseReportRequestForm.states';
import {
    Content,
    ContentInner,
    FieldsWrapper,
    MemoEditor,
    Root,
    SectionContainerAligned,
    StyledField,
    Toolbar,
    ToolbarText,
} from './NetSuiteExpenseReportRequestForm.styles';
import { NetSuiteExpenseReportRequestFormProps } from './NetSuiteExpenseReportRequestForm.types';

export const NetSuiteExpenseReportRequestForm: FC<NetSuiteExpenseReportRequestFormProps> = memo((props) => {
    const { request } = props;

    const pageContext = useSelector(getContext);
    const setFieldValues = useSetAtom(netSuiteExpenseReportFieldValuesState);
    const setCustomFieldsValues = useSetAtom(netSuiteCustomFieldsState);
    const setCompanyId = useSetAtom(companyIdState);

    const expenseLines = useAtomValue(netSuiteExpenseLinesState);

    const {
        fieldValues,
        fieldSettings,
        exchangeRatesData,
        currencies,
        requestCurrencyISOCode,
        onDetailsChange,
        onCurrencyChange,
    } = useExpenseReportForm(request);

    const resetExpenseReportFormState = useResetExpenseReportFormState();

    const { companyId, companyCurrency } = request;

    const { showErrors, getValidationErrors, getRequestTransfer, attachmentsFieldSettings } =
        useExpenseReportFormSubmit(request, companyCurrency, requestCurrencyISOCode, exchangeRatesData);

    const { employees, departments, locations, classes } = useSelector(getNetSuiteContext);

    const { totalExpenses, advanceToApply, totalReimbursableAmount } = useExpenseReportAmount();

    const { setManyExpenseLines } = useExpenseReportExpenseLines();

    const currencyPrecision = useCurrencyPrecision(fieldValues.currency ?? request.details.currency);

    useLifecycles(
        () => {
            const {
                exchangeRateSource,
                details: { customFields, expenseLines: requestExpenseLines, ...fields },
            } = request;

            setFieldValues({
                ...fields,
                date: fields.date || moment().toISOString(),
                exchangeRate: exchangeRateSource === domain.ExchangeRateSource.Manual ? fields.exchangeRate : null,
            });
            setCustomFieldsValues(
                customFields && customFields.length > 0 ? convertCustomFieldsToState(customFields) : {}
            );
            setManyExpenseLines(requestExpenseLines, currencyPrecision);
            setCompanyId(companyId);
        },
        () => {
            resetExpenseReportFormState();
        }
    );

    const onUploadAttachments = useExpenseReportFilesUploadAttachments(request, expenseLines);

    const onChangeEmployee = useCallback(
        (value: (typeof employees)[number] | null) => {
            onDetailsChange('employee')(value);

            const { currency } = fieldValues;

            if (value && (!currency || !value.currencyIds.includes(currency.id))) {
                const currencyDefault = currencies.find((el) => el.id === value.defaultCurrencyId) || null;

                onDetailsChange('currency')(currencyDefault);
            }
        },
        [currencies, fieldValues, onDetailsChange]
    );

    useExpenseLinesUpdate(request, fieldValues.currency, fieldValues.date);

    const { isBelgradeUIFilesUploadSection, isBelgradeUINetSuiteExpensesSection } = useUserBetaFeatures();

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider maxSize={pageContext.attachmentMaxSize} onUpload={onUploadAttachments}>
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarText>{messages.title}</ToolbarText>

                    <ActionsBlock
                        customGetRequestTransfer={getRequestTransfer}
                        customRequestValidationFn={getValidationErrors}
                    />
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <SectionContainer title={messages.sectionDetailsTitle}>
                            <FieldsWrapper>
                                {fieldSettings.employee !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldEmployee}
                                        required={fieldSettings.employee === domain.DocumentFieldState.Mandatory}
                                    >
                                        <PlainDataProvider items={employees || []}>
                                            <DropdownEditor
                                                value={fieldValues.employee || null}
                                                singleLinePerItem={false}
                                                onChange={onChangeEmployee}
                                                placeholder={messages.fieldEmployee}
                                                disabled={fieldSettings.employee === domain.DocumentFieldState.ReadOnly}
                                                invalid={
                                                    showErrors &&
                                                    !fieldValues.employee &&
                                                    fieldSettings.employee === domain.DocumentFieldState.Mandatory
                                                }
                                                loading={false}
                                            />
                                        </PlainDataProvider>
                                    </StyledField>
                                )}

                                {fieldSettings.advance !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldAdvance}
                                        required={fieldSettings.advance === domain.DocumentFieldState.Mandatory}
                                    >
                                        <NumberEditor
                                            allowFloat
                                            displayFormat='currency'
                                            value={typeof fieldValues.advance === 'number' ? fieldValues.advance : null}
                                            placeholder={messages.fieldAdvance}
                                            max={constants.netSuiteConstants.advanceMaxValue}
                                            min={0}
                                            precision={currencyPrecision}
                                            disabled={fieldSettings.advance === domain.DocumentFieldState.ReadOnly}
                                            invalid={
                                                showErrors &&
                                                fieldSettings.advance === domain.DocumentFieldState.Mandatory &&
                                                (fieldValues.advance == null || fieldValues.advance < 0)
                                            }
                                            onChange={onDetailsChange('advance')}
                                        />
                                    </StyledField>
                                )}

                                {fieldSettings.date !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldTransactionDate}
                                        required={fieldSettings.date === domain.DocumentFieldState.Mandatory}
                                    >
                                        <DateTimeEditor
                                            value={fieldValues.date || null}
                                            placeholder={messages.fieldTransactionDate}
                                            onChange={onDetailsChange('date')}
                                            disabled={fieldSettings.date === domain.DocumentFieldState.ReadOnly}
                                            invalid={
                                                showErrors &&
                                                !fieldValues.date &&
                                                fieldSettings.date === domain.DocumentFieldState.Mandatory
                                            }
                                        />
                                    </StyledField>
                                )}

                                {fieldSettings.dueDate !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldDueDate}
                                        required={fieldSettings.dueDate === domain.DocumentFieldState.Mandatory}
                                    >
                                        <DateTimeEditor
                                            value={fieldValues.dueDate || null}
                                            placeholder={messages.fieldDueDate}
                                            onChange={onDetailsChange('dueDate')}
                                            disabled={fieldSettings.dueDate === domain.DocumentFieldState.ReadOnly}
                                            invalid={
                                                showErrors &&
                                                !fieldValues.dueDate &&
                                                fieldSettings.dueDate === domain.DocumentFieldState.Mandatory
                                            }
                                        />
                                    </StyledField>
                                )}

                                {fieldSettings.department !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldDepartment}
                                        required={fieldSettings.department === domain.DocumentFieldState.Mandatory}
                                    >
                                        <PlainDataProvider items={departments || []}>
                                            <DropdownEditor
                                                value={fieldValues.department || null}
                                                singleLinePerItem={false}
                                                onChange={onDetailsChange('department')}
                                                placeholder={messages.fieldDepartment}
                                                disabled={
                                                    fieldSettings.department === domain.DocumentFieldState.ReadOnly
                                                }
                                                invalid={
                                                    showErrors &&
                                                    !fieldValues.department &&
                                                    fieldSettings.department === domain.DocumentFieldState.Mandatory
                                                }
                                            />
                                        </PlainDataProvider>
                                    </StyledField>
                                )}

                                {fieldSettings.class !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldClass}
                                        required={fieldSettings.class === domain.DocumentFieldState.Mandatory}
                                    >
                                        <PlainDataProvider items={classes || []}>
                                            <DropdownEditor
                                                value={fieldValues.class || null}
                                                singleLinePerItem={false}
                                                onChange={onDetailsChange('class')}
                                                placeholder={messages.fieldClass}
                                                disabled={fieldSettings.class === domain.DocumentFieldState.ReadOnly}
                                                invalid={
                                                    showErrors &&
                                                    !fieldValues.class &&
                                                    fieldSettings.class === domain.DocumentFieldState.Mandatory
                                                }
                                            />
                                        </PlainDataProvider>
                                    </StyledField>
                                )}

                                {fieldSettings.location !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldLocation}
                                        required={fieldSettings.location === domain.DocumentFieldState.Mandatory}
                                    >
                                        <PlainDataProvider items={locations || []}>
                                            <DropdownEditor
                                                value={fieldValues.location || null}
                                                singleLinePerItem={false}
                                                onChange={onDetailsChange('location')}
                                                placeholder={messages.fieldLocation}
                                                disabled={fieldSettings.location === domain.DocumentFieldState.ReadOnly}
                                                invalid={
                                                    showErrors &&
                                                    !fieldValues.location &&
                                                    fieldSettings.location === domain.DocumentFieldState.Mandatory
                                                }
                                            />
                                        </PlainDataProvider>
                                    </StyledField>
                                )}

                                {fieldSettings.accountForCorporateCardExpenses !== domain.DocumentFieldState.Hidden && (
                                    <StyledField
                                        title={messages.fieldAccountForCorporateCardExpenses}
                                        required={
                                            checkAccountForCorporateCardIsInvalid(fieldValues, expenseLines) ||
                                            fieldSettings.accountForCorporateCardExpenses ===
                                                domain.DocumentFieldState.Mandatory
                                        }
                                    >
                                        <NetSuiteAccountsCorporateCardDataProvider
                                            currency={fieldValues.currency || null}
                                        >
                                            <DropdownEditor
                                                value={fieldValues.accountForCorporateCardExpenses || null}
                                                singleLinePerItem={false}
                                                onChange={onDetailsChange('accountForCorporateCardExpenses')}
                                                placeholder={messages.fieldAccountForCorporateCardExpenses}
                                                disabled={
                                                    fieldSettings.accountForCorporateCardExpenses ===
                                                    domain.DocumentFieldState.ReadOnly
                                                }
                                                invalid={
                                                    showErrors &&
                                                    (checkAccountForCorporateCardIsInvalid(fieldValues, expenseLines) ||
                                                        fieldSettings.accountForCorporateCardExpenses ===
                                                            domain.DocumentFieldState.Mandatory)
                                                }
                                            />
                                        </NetSuiteAccountsCorporateCardDataProvider>
                                    </StyledField>
                                )}

                                <NetSuiteCustomFields workflowId={request.template.id} showErrors={showErrors} />
                            </FieldsWrapper>
                        </SectionContainer>

                        {fieldSettings.memo !== domain.DocumentFieldState.Hidden && (
                            <SectionContainer title={messages.fieldPurpose}>
                                <MemoEditor
                                    value={fieldValues.memo || ''}
                                    onChange={onDetailsChange('memo')}
                                    placeholder={messages.fieldPurpose}
                                    changeOnBlur
                                    invalid={
                                        showErrors &&
                                        !fieldValues.memo &&
                                        fieldSettings.memo === domain.DocumentFieldState.Mandatory
                                    }
                                    minHeight={5}
                                    maxLength={constants.netSuiteConstants.purposeMaxLength}
                                    disabled={fieldSettings.memo === domain.DocumentFieldState.ReadOnly}
                                />
                            </SectionContainer>
                        )}

                        <NetSuiteCurrencySection
                            currencies={currencies}
                            fieldValues={fieldValues}
                            fieldSettings={fieldSettings}
                            onExchangeRateChange={onDetailsChange('exchangeRate')}
                            onCurrencyChange={onCurrencyChange}
                        />

                        {isBelgradeUINetSuiteExpensesSection ? (
                            // Box is temporary solution to shift the section for reorderable icon visibility
                            <Box spacing='0 60'>
                                <ExpenseSection />
                            </Box>
                        ) : (
                            <ExpenseLines
                                requestId={request.id}
                                workflowId={request.template.id}
                                currency={fieldValues.currency}
                                requestDate={fieldValues.date}
                                requestAttachments={request.attachments}
                            />
                        )}

                        <SectionContainerAligned title=''>
                            <AmountInfoExpenseReport
                                currency={fieldValues.currency}
                                totalExpenses={totalExpenses}
                                advance={advanceToApply}
                                reimbursableAmount={totalReimbursableAmount}
                            />
                        </SectionContainerAligned>

                        {isFieldVisible(attachmentsFieldSettings.attachments) &&
                            (isBelgradeUIFilesUploadSection ? (
                                <Grid gridTemplateColumns='50%' gap={16}>
                                    <AttachmentsSection
                                        request={request}
                                        required={isFieldMandatory(attachmentsFieldSettings.attachments)}
                                    />
                                </Grid>
                            ) : (
                                <FilesSection required={isFieldMandatory(attachmentsFieldSettings.attachments)} />
                            ))}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

NetSuiteExpenseReportRequestForm.displayName = 'NetSuiteExpenseReportRequestForm';
